import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Primary button atom. Variants per Ink & Ember design system (primitives.jsx Btn).
 *
 *   <Btn kind="primary" size="md">Get matched</Btn>
 *   <Btn kind="ghost" icon={<Phone size={16} />} size="lg">(213) 555-0148</Btn>
 */

export type BtnKind = 'primary' | 'secondary' | 'ghost' | 'danger';
export type BtnSize = 'sm' | 'md' | 'lg' | 'xl';

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    kind?: BtnKind;
    size?: BtnSize;
    icon?: ReactNode;
    full?: boolean;
    loading?: boolean;
}

const KIND_CLASSES: Record<BtnKind, string> = {
    primary:
        'bg-ember text-paper hover:bg-ember-deep active:bg-ember-deep disabled:bg-rule disabled:text-ink-mute',
    secondary:
        'bg-card text-ink border border-rule hover:border-ink-soft disabled:text-ink-mute disabled:bg-paper-deep',
    ghost: 'bg-transparent text-ink hover:bg-paper-deep disabled:text-ink-mute',
    danger: 'bg-card text-ember-deep border border-ember-tint hover:bg-ember-tint disabled:opacity-60',
};

const SIZE_CLASSES: Record<BtnSize, string> = {
    sm: 'h-9 px-3.5 text-[14px] gap-1.5',
    md: 'h-11 px-4.5 text-[15px] gap-2',
    lg: 'h-13 px-5.5 text-[16px] gap-2',
    xl: 'h-15 px-7 text-[17px] gap-2.5',
};

export const Btn = forwardRef<HTMLButtonElement, BtnProps>(function Btn(
    {
        kind = 'primary',
        size = 'md',
        icon,
        full = false,
        loading = false,
        disabled,
        className,
        children,
        type = 'button',
        ...rest
    },
    ref,
) {
    const cls = [
        'inline-flex items-center justify-center font-medium tracking-tight transition-colors',
        'rounded-md cursor-pointer disabled:cursor-not-allowed',
        SIZE_CLASSES[size],
        KIND_CLASSES[kind],
        full ? 'w-full' : '',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <button
            ref={ref}
            type={type}
            className={cls}
            disabled={disabled === true || loading}
            aria-busy={loading || undefined}
            {...rest}
        >
            {loading ? (
                <span
                    aria-hidden="true"
                    className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                />
            ) : (
                icon
            )}
            <span>{children}</span>
        </button>
    );
});

export default Btn;
